import { get, post } from "@/services/http/platformServer";
import { PagedParams, PagedResult } from "./types";
import moment from "moment";
import ShopType from "@/enum/ShopType";

export interface PrepaidAccount {
    id: number;
    /**余额 (分) */
    balance: number;
    /**累积充值 (分) */
    cumulativeAmount: number;
}

export function getPrepaidAccount(shopId: number) {
    return get<PrepaidAccount>(shopId, "/prepaid/account/get", {
        shopId,
    });
}

interface ReloadPaymentHtmlDto {
    /**
     * 支付页面HTML
     * 用于给商家扫描支付
     */
    paymentHtml: string;
    /**
     * 充值记录ID
     */
    reloadRecordId: number;
}

/**
 * 支付
 */
export function payPrepaidAccountReload(shopId: number, amount: number) {
    return post<ReloadPaymentHtmlDto>(shopId, "/prepaid/reload/pay", {
        shopId,
        amount,
    });
}

/**
 * 预支付账号支付状态
 */
export enum PrepaidReloadRecordStatus {
    CANCELED = "CANCELED",
    CLOSE = "CLOSE",
    PAID = "PAID",
    REFUND = "REFUND",
    UNPAID = "UNPAID",
}

export const PrepaidReloadRecordStatusNameMapping = {
    [PrepaidReloadRecordStatus.CANCELED]: "已取消",
    [PrepaidReloadRecordStatus.PAID]: "已支付",
    [PrepaidReloadRecordStatus.REFUND]: "已退款",
    [PrepaidReloadRecordStatus.UNPAID]: "未支付",
};

/**
 * 预支付账号支付记录
 */
export interface PrepaidReloadRecord {
    /**
     * 充值金额，单位：分
     */
    amount: number;
    /**
     * 当前充值交易成功后的余额（该余额仅与当前交易有关，不代表用户当前余额）
     */
    balance: number;
    /**
     * 创建时间
     */
    dateCreated: string;
    /**
     * 充值记录ID
     */
    id: number;
    /**
     * 支付状态
     */
    status: PrepaidReloadRecordStatus;
    /**
     * 支付平台的交易号
     */
    tradeNo: string;

    /**
     * ❗目前此值由前端上下文赋值上去
     */
    shopType?: ShopType;
}

interface GetRecordListParams extends PagedParams {
    shopId: number;
}

/**
 * 获取店铺预付款支付记录列表
 */
export function getPrepaidAccountPaymentRecordList(params: GetRecordListParams) {
    return post<PagedResult<PrepaidReloadRecord>>(params.shopId, "/prepaid/reload/list", params);
}

export enum PrepaidAccountConsumptionRecordType {
    TAOBAO_ORDER_DECRYPTION = "TAOBAO_ORDER_DECRYPTION",
    PURCHASE_ORDER_VOLUME = "PURCHASE_ORDER_VOLUME",
}

export const PrepaidAccountConsumptionRecordTypeNameMapping = {
    [PrepaidAccountConsumptionRecordType.TAOBAO_ORDER_DECRYPTION]: "淘宝订单解密",
    [PrepaidAccountConsumptionRecordType.PURCHASE_ORDER_VOLUME]: "购买订单量",
};
export interface PrepaidAccountConsumptionRecord {
    id: number;
    amount: number;
    businessId: string;
    dateCreated: string;
    prepaidAccountId: number;
    type: PrepaidAccountConsumptionRecordType;
}

/**
 * 获取店铺预付款消费记录列表
 */
export function getPrepaidAccountConsumeRecordList(params: GetRecordListParams) {
    return post<PagedResult<PrepaidAccountConsumptionRecord>>(params.shopId, "/prepaid_account/consumeRecords", params);
}

interface ReloadRecordPaymentStatus {
    /**
     * 充值记录ID
     */
    reloadRecordId: number;
    /**
     * 支付状态
     */
    status: PrepaidReloadRecordStatus;
}

/**
 * 同步预付款支付状态
 * 正常的话支付系统回调到发货易的就会改变，还有支付系统支付成功后也会发消息通知发货易也会改变，
 * 理论上是为了预防上面回调和通知都出现问题时的一种手工触发
 */
export function syncPrepaidAccountPaymentStatus(shopId: number, reloadRecordId: number) {
    return post<ReloadRecordPaymentStatus>(shopId, "/prepaid/reload/sync_payment_status", {
        reloadRecordId,
    });
}

/**
 * 获取充值记录
 */
export function getPrepaidReload(shopId: number, id: number) {
    return get<PrepaidReloadRecord>(shopId, `/prepaid/reload/get/${id}`);
}

/**
 * 同步第一页的待支付订单
 */
export async function syncFirstPagePrepaidAccountPaymentStatus(shopId: number) {
    const result = await getPrepaidAccountPaymentRecordList({
        shopId,
        pageNo: 1,
        pageSize: 10,
    });

    for (let i = 0; i < result?.items?.length; i++) {
        const item = result.items[i];

        // 是否是一天之内的时间
        if (moment().diff(moment(item.dateCreated), "days") < 1) {
            if (item.status === PrepaidReloadRecordStatus.UNPAID) {
                await syncPrepaidAccountPaymentStatus(shopId, item.id);
            }
        }
    }
}

interface TBFuwuSkuGetResultArticleItemViewUnit {
    actualPrice: string;
    canSub: boolean;
    cycNum: number;
    cycUnit: number;
    errorCode: string;
    errorMsg: string;
    itemCode: string;
    itemName: string;
    originPrice: string;
    promPrice: string;
    quantity: number;
}

interface TBFuwuSkuGetResult {
    varticleCode: string;
    articleCommment: string;
    articleItemViewUnits: TBFuwuSkuGetResultArticleItemViewUnit[];
    articleName: string;
    errorCode: string;
    errorMsg: string;
    nick: string;
    pictUrl: string;
}

/**
 * 获取淘宝服务市场内购可以订购的商品
 */
export function getTBPrePaidAccountSku(shopId: number) {
    return get<TBFuwuSkuGetResult>(shopId, "/taobao_pre_paid_account/sku", {
        shopId,
    });
}

interface TbPurchaseOrderConfirmParams {
    cycNum: string;
    cycUnit: string;
    itemCode: string;
    quantity: number;
    shopId: number;
}

interface TbPurchaseOrderConfirmResult {
    url: string;
    success: boolean;
    message: string;
    subMsg: string;
    subMessage: string;
}

/**
 * 淘宝下单确认获取支付链接
 */
export function tbPrePaidAccountPurchaseOrderConfirm(params: TbPurchaseOrderConfirmParams) {
    return get<TbPurchaseOrderConfirmResult>(params.shopId, "/taobao_pre_paid_account/purchase_order_confirm", params);
}

/**
 * 同步淘宝已支付的订单
 */
export function syncTbPrePaidAccountOrder(shopId: number) {
    return get(shopId, "/taobao_pre_paid_account/order/sync", {
        shopId,
    });
}
